import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, ElementRef, inject, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import { QuillEditorComponent } from 'ngx-quill';
import { combineLatest, finalize, switchMap, take } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { ImageViewerComponent } from 'src/app/shared/components/image-viewer/image-viewer.component';
import { SelectListComponent, SelectListData } from 'src/app/shared/components/select-list/select-list.component';
import { SelectComponent, SelectOption } from 'src/app/shared/components/ui/select/select.component';
import { InputComponent } from 'src/app/shared/components/ui/input/input.component';
import { AiRestrictedHoverDirective } from 'src/app/shared/directives/ai-restricted-hover.directive';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { UploadService } from 'src/app/shared/services/upload/upload.service';
import { isFileAllowed } from 'src/app/shared/utils/file-validation.utils';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { AIPaneData, ContentStateService } from 'src/app/views/content/content-state.service';
import { CourseSettings } from 'src/app/views/displayer/courses/courses.dto';
import { ModelsService } from 'src/app/views/my-space/my-models/models.service';
import { CheckboxComponent } from '../../../shared/components/ui/checkbox/checkbox.component';
import { TemplatePickerComponent } from 'src/app/shared/components/template-picker/template-picker.component';
import { MermaidDialogComponent } from '../chunk/chunk-displayer/displayers/mermaid-dialog.component';
import { CopyImageAndCreateChunkData } from '../chunk/chunk.dto';
import { ContentSearcherComponent, ContentSearchResult } from '../content-searcher/content-searcher.component';
import { Conversation, ConversationMessage, ConversationService } from '../conversation.service';
import { QuizComponent } from './components/quiz/quiz.component';

type GenerationMode = 'text' | 'image' | 'content' | 'mermaid' | 'quiz' | 'settings';

@Component({
  selector: 'app-generate-content-pane',
  templateUrl: './generate-content-pane.component.html',
  styleUrls: ['./generate-content-pane.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MarkdownModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    TranslateModule,
    TranslateModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatSlideToggleModule,
    QuillEditorComponent,
    QuizComponent,
    ImageViewerComponent,
    MatDialogModule,
    AiRestrictedHoverDirective,
    SelectComponent,
    InputComponent,
    CheckboxComponent,
    TemplatePickerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenerateContentPaneComponent implements OnInit {
  @ViewChild('scrollContainer') private scrollContainer: ElementRef;

  // --- Component Inputs/Outputs ---
  contentID = input.required<string>();
  paneData = input<AIPaneData | null>(null);
  settings = input<CourseSettings | null>(null);
  closed = output<any>();
  settingsChanged = output<CourseSettings>();
  // --- State Properties ---
  activeInputMode: GenerationMode = 'text';
  prompt = '';
  attachedFiles = signal<{ id: string; file: File; url?: string; type: 'audio' | 'file' | 'video' | 'image'; name: string; uploading?: boolean }[]>([]);

  loading = signal(false);
  now = new Date();

  // --- Services ---
  private conversationService = inject(ConversationService);
  private modelService = inject(ModelsService);
  private uploadService = inject(UploadService);
  private env = inject(EnvService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private chunkService = inject(ChunkService);
  private contentStateService = inject(ContentStateService);
  private errorHandler = inject(ErrorHandlerService);
  private overlay = inject(DynamicOverlayService);
  private storage = inject(Storage);
  private cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private confirmMenuService = inject(ConfirmMenuService);
  private dialog = inject(MatDialog);
  private http = inject(HttpClient);

  // --- Chat Properties ---
  private conversation$ = toObservable(this.contentID).pipe(switchMap((id) => this.conversationService.getConversationFromCache(id)));
  conversation = toSignal(this.conversation$);
  processedConversation = signal<Conversation | undefined>(undefined);

  // --- Image Generation Properties ---
  selectedAspectRatio = '16:9';
  selectedGuidanceScale = 18;
  aspectRatios = [
    { label: 'MySpace.AIImageResolutions.Square', value: '1:1' },
    { label: 'MySpace.AIImageResolutions.Portrait', value: '9:16' },
    { label: 'MySpace.AIImageResolutions.Landscape', value: '16:9' },
  ];
  guidanceScaleOptions = [
    { label: 'MySpace.AIGuidanceScale.Low', value: 10 },
    { label: 'MySpace.AIGuidanceScale.Medium', value: 18 },
    { label: 'MySpace.AIGuidanceScale.High', value: 25 },
  ];
  isUploading = false;
  uploadSuccess = false;

  useContentContext = signal(true);
  allowAiSearch = computed(() => this.settings()?.allowAiSearch ?? true);
  aiSkillztankBoundary = computed(() => this.settings()?.aiSkillztankBoundary ?? true);
  searchAllContent = computed(() => this.settings()?.searchAllContent ?? false);
  attachedExternalContexts = signal<{ id: string; title: string; type: string; fullText?: string; name?: string }[]>([]);
  contentName = computed(() => this.contentStateService.contentName || this.translate.instant('GenerateContent.CurrentContent'));
  isReader = this.contentStateService.isReader;
  cannotUseAI = computed(() => !this.authService.canUseAI());

  dragCounter = 0;
  isDraggingOver = signal(false);

  quillModules = { toolbar: false };

  toggleAiSearch(checked: boolean) {
    const current = this.settings() || {};
    this.settingsChanged.emit({ ...current, allowAiSearch: checked });
  }

  toggleAiSkillztankBoundary(checked: boolean) {
    const current = this.settings() || {};
    this.settingsChanged.emit({ ...current, aiSkillztankBoundary: checked });
  }

  toggleSearchAllContent(checked: boolean) {
    const current = this.settings() || {};
    this.settingsChanged.emit({ ...current, searchAllContent: checked });
  }

  // --- Mermaid Inline Form ---
  mermaidForm = new FormGroup({
    subject: new FormControl('', [Validators.required]),
    diagramType: new FormControl('auto'),
    customization: new FormControl(''),
    model: new FormControl<'fast' | 'pro'>('fast', [Validators.required]),
  });

  diagramTypeOptions: SelectOption[] = [
    { value: 'auto', label: 'GenerateContent.Mermaid.TypeAuto' },
    { value: 'flowchart', label: 'GenerateContent.Mermaid.TypeFlowchart' },
    { value: 'sequenceDiagram', label: 'GenerateContent.Mermaid.TypeSequence' },
    { value: 'classDiagram', label: 'GenerateContent.Mermaid.TypeClass' },
    { value: 'stateDiagram', label: 'GenerateContent.Mermaid.TypeState' },
    { value: 'erDiagram', label: 'GenerateContent.Mermaid.TypeER' },
    { value: 'gantt', label: 'GenerateContent.Mermaid.TypeGantt' },
    { value: 'pie', label: 'GenerateContent.Mermaid.TypePie' },
    { value: 'gitGraph', label: 'GenerateContent.Mermaid.TypeGit' },
    { value: 'mindmap', label: 'GenerateContent.Mermaid.TypeMindmap' },
    { value: 'timeline', label: 'GenerateContent.Mermaid.TypeTimeline' },
    { value: 'graph', label: 'GenerateContent.Mermaid.TypeGraph' },
  ];

  modelOptions: SelectOption[] = [
    { value: 'fast', label: 'GenerateContent.Mermaid.ModelFast' },
    { value: 'pro', label: 'GenerateContent.Mermaid.ModelPro' },
  ];

  // --- Image Inline Form ---
  imageForm = new FormGroup({
    prompt: new FormControl('', [Validators.required]),
    aspectRatio: new FormControl('16:9', [Validators.required]),
    model: new FormControl<'fast' | 'pro'>('pro', [Validators.required]),
  });

  aspectRatioOptions: SelectOption[] = [
    { value: '1:1', label: 'ImageGenerator.AspectRatio.Square' },
    { value: '16:9', label: 'ImageGenerator.AspectRatio.Landscape' },
    { value: '9:16', label: 'ImageGenerator.AspectRatio.Portrait' },
    { value: '4:3', label: 'ImageGenerator.AspectRatio.Standard' },
    { value: '3:4', label: 'ImageGenerator.AspectRatio.Portrait34' },
  ];

  imageModelOptions: SelectOption[] = [
    { value: 'fast', label: 'ImageGenerator.Model.Fast' },
    { value: 'pro', label: 'ImageGenerator.Model.Pro' },
  ];

  // --- Content Inline Form ---
  contentForm = new FormGroup({
    subject: new FormControl('', [Validators.required]),
    customization: new FormControl(''),
    model: new FormControl<'fast' | 'pro'>('fast', [Validators.required]),
    template: new FormControl(localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none'),
  });

  contentModelOptions: SelectOption[] = [
    { value: 'fast', label: 'ContentGenerator.FastModel' },
    { value: 'pro', label: 'ContentGenerator.ProModel' },
  ];

  // --- Quiz Inline Form ---
  quizForm = new FormGroup({
    subject: new FormControl('', [Validators.required]),
    count: new FormControl(5, [Validators.required, Validators.min(1)]),
    quizType: new FormControl<'multiple-choice' | 'free-text' | 'mixed'>('mixed', [Validators.required]),
    difficulty: new FormControl<'easy' | 'medium' | 'hard'>('medium', [Validators.required]),
    additionalContext: new FormControl(''),
  });

  quizTypeOptions: SelectOption[] = [
    { value: 'mixed', label: 'Quiz.TypeMix' },
    { value: 'multiple-choice', label: 'Quiz.TypeMultipleChoice' },
    { value: 'free-text', label: 'Quiz.TypeFreeText' },
  ];

  constructor() {
    effect(() => {
      const conv = this.conversation();
      if (conv) {
        this.processConversationImages(conv);
      } else {
        this.processedConversation.set(undefined);
      }
    });

    effect(() => {
      // Scroll when loading starts or when new messages are added
      if (this.loading() || this.processedConversation()) {
        setTimeout(() => this.scrollToBottom(), 100);
      }
    });
  }

  ngOnInit(): void {
    this.conversationService.syncConversationIfNeeded(this.contentID());

    // Wait for both templates and user config to load
    combineLatest([this.modelService.getTemplates(), this.authService.userConfiguration$])
      .pipe(take(1))
      .subscribe(([templates, userConfig]) => {
        this.cdr.markForCheck();
      });
  }

  updateTemplateName(templateId: string) {
    // No-op
  }

  toggleContext() {
    this.useContentContext.update((v) => !v);
  }

  toggleSettingsMode(): void {
    if (this.activeInputMode === 'settings') {
      this.activeInputMode = 'text';
    } else {
      this.activeInputMode = 'settings';
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isReader() && !this.cannotUseAI()) {
      this.dragCounter++;
      if (this.dragCounter === 1) {
        this.isDraggingOver.set(true);
      }
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isReader()) {
      this.dragCounter--;
      if (this.dragCounter <= 0) {
        this.dragCounter = 0;
        this.isDraggingOver.set(false);
      }
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDraggingOver.set(false);

    if (this.isReader() || this.cannotUseAI()) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  async handleFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!isFileAllowed(file)) {
        this.toast.snackbar.open(this.translate.instant('FileUploader.UnsupportedFileType') || 'File type not supported', 'Close', { duration: 3000 });
        continue;
      }

      let mediaType: 'audio' | 'file' | 'video' | 'image' = 'file';
      if (file.type.startsWith('audio/')) mediaType = 'audio';
      else if (file.type.startsWith('video/')) mediaType = 'video';
      else if (file.type.startsWith('image/')) mediaType = 'image';

      const fileId = Date.now().toString() + '_' + i;

      this.attachedFiles.update((filesArray) => [...filesArray, { id: fileId, file, name: file.name, type: mediaType, uploading: true }]);

      this.uploadFileInternal(file, fileId);
    }
  }

  async onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    this.handleFiles(files);
    event.target.value = '';
  }

  private async uploadFileInternal(file: File, fileId: string) {
    try {
      const filename = `conversation_attachments/${this.contentStateService.courseId || this.contentStateService.noteId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const snapshot: any = await this.authService.uploadFile(file, filename, undefined, true);
      const gsUrl = `gs://${snapshot.ref.bucket}/${snapshot.ref.fullPath}`;

      this.attachedFiles.update((filesArray) => filesArray.map((f) => (f.id === fileId ? { ...f, url: gsUrl, uploading: false } : f)));
    } catch (err) {
      console.error('File upload failed', err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Upload failed', 'Close', { duration: 3000 });
      this.removeAttachment(fileId);
    }
  }

  removeAttachment(fileId: string) {
    this.attachedFiles.update((filesArray) => filesArray.filter((f) => f.id !== fileId));
  }

  async processConversationImages(conv: Conversation): Promise<void> {
    const messages = await Promise.all(
      conv.messages.map(async (message) => {
        if (message.type === 'image') {
          try {
            const displayUrl = await this.getDownloadURL(message.content.imageUrl);
            return { ...message, content: { ...message.content, displayUrl } };
          } catch (error) {
            console.warn('Failed to load image URL for message:', error);
            // Return message without displayUrl, viewer should handle it
            return message;
          }
        }
        return message;
      })
    );
    this.processedConversation.set({ ...conv, messages });
  }

  handleGenerateClick(): void {
    this.sendMessage();
  }

  onEditorKeyDown(event: KeyboardEvent): void {
    if (this.cannotUseAI()) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.handleGenerateClick();
    }
  }

  toggleMode(mode: GenerationMode): void {
    if (mode === 'image' && this.activeInputMode !== 'image') {
      this.imageForm.reset({ prompt: '', aspectRatio: '16:9', model: 'pro' });
    }
    if (mode === 'content' && this.activeInputMode !== 'content') {
      this.contentForm.reset({
        subject: '',
        customization: '',
        model: 'fast',
        template: localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none',
      });
      const lectureSubject = this.contentStateService.lecture?.subject;
      if (lectureSubject) {
        this.contentForm.patchValue({ subject: lectureSubject });
      }
    }
    if (mode === 'quiz' && this.activeInputMode !== 'quiz') {
      this.quizForm.reset({ subject: '', count: 5, quizType: 'mixed', difficulty: 'medium', additionalContext: '' });
      const lectureSubject = this.contentStateService.lecture?.subject;
      if (lectureSubject) {
        this.quizForm.patchValue({ subject: lectureSubject });
      }
    }
    this.activeInputMode = this.activeInputMode === mode ? 'text' : mode;
  }

  sendMessage(): void {
    const trimmedPrompt = this.prompt ? this.prompt.trim() : '';
    if (!trimmedPrompt) return;

    this.loading.set(true);

    let messageType: any = 'text';
    if (this.activeInputMode === 'image') messageType = 'image_request';
    if (this.activeInputMode === 'content') messageType = 'content_request';

    const attached = this.attachedFiles();
    const readyAttachments = attached.filter((a) => !a.uploading && a.url);

    const userMessage: ConversationMessage = {
      role: 'user',
      type: messageType,
      content: trimmedPrompt,
    };

    if (readyAttachments.length > 0) {
      (userMessage as any).mediaContexts = readyAttachments.map((a) => ({ url: a.url, type: a.type, filename: a.name }));
      userMessage.files = readyAttachments.map((a) => a.url);
    }

    this.prompt = '';

    let context = this.useContentContext() ? this.contentStateService.getFullContentText() : '';
    const externalContexts = this.attachedExternalContexts();
    const attachedContentIds = externalContexts.map((c) => c.id);

    const links = this.attachedFiles().map((file) => file.url);

    this.attachedFiles.set([]);
    this.conversationService
      .addMessageAndGenerateResponse(this.contentID(), userMessage, context, links, attachedContentIds, {
        allowAiSearch: this.allowAiSearch(),
        aiSkillztankBoundary: this.aiSkillztankBoundary(),
        searchAllContent: this.searchAllContent(),
        courseId: this.contentStateService.courseId,
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.scrollToBottom();
        })
      )
      .subscribe({
        next: (conversation) => {},
        error: (err) => {
          console.error('Error sending message:', err);
          this.errorHandler.handleAiRestrictedError(err);
        },
      });
  }

  startGenerateContent(): void {
    if (this.activeInputMode === 'content') {
      this.activeInputMode = 'text';
    } else {
      this.contentForm.reset({
        subject: '',
        customization: '',
        model: 'fast',
        template: localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none',
      });
      const lectureSubject = this.contentStateService.lecture?.subject;
      if (lectureSubject) {
        this.contentForm.patchValue({ subject: lectureSubject });
      }
      this.activeInputMode = 'content';
    }
  }

  generateMermaid(): void {
    if (this.activeInputMode === 'mermaid') {
      this.activeInputMode = 'text';
    } else {
      this.mermaidForm.reset({ subject: '', diagramType: 'auto', customization: '', model: 'fast' });
      this.activeInputMode = 'mermaid';
    }
  }

  cancelMermaid(): void {
    this.activeInputMode = 'text';
    this.mermaidForm.reset({ subject: '', diagramType: 'auto', customization: '', model: 'fast' });
  }

  submitMermaid(): void {
    if (this.mermaidForm.invalid) {
      this.mermaidForm.markAllAsTouched();
      return;
    }

    const { subject, diagramType, customization, model } = this.mermaidForm.getRawValue();
    this.activeInputMode = 'text';
    this.loading.set(true);

    const userMessage: ConversationMessage = {
      role: 'user',
      type: 'mermaid_request',
      content: subject!,
      context: JSON.stringify({ diagramType, customization, model }),
    };

    const context = this.useContentContext() ? this.contentStateService.getFullContentText() : '';

    this.conversationService.addMessageAndGenerateResponse(this.contentID(), userMessage, context, [], []).subscribe({
      next: () => {
        this.loading.set(false);
        this.scrollToBottom();
      },
      error: (err) => {
        console.error('Mermaid generation failed', err);
        this.errorHandler.handleAiRestrictedError(err);
        this.loading.set(false);
      },
    });

    this.mermaidForm.reset({ subject: '', diagramType: 'auto', customization: '', model: 'fast' });
  }

  cancelImage(): void {
    this.activeInputMode = 'text';
    this.imageForm.reset({ prompt: '', aspectRatio: '16:9', model: 'pro' });
  }

  submitImage(): void {
    if (this.imageForm.invalid) {
      this.imageForm.markAllAsTouched();
      return;
    }

    const { prompt, aspectRatio, model } = this.imageForm.getRawValue();
    this.activeInputMode = 'text';
    this.loading.set(true);

    const userMessage: ConversationMessage = {
      role: 'user',
      type: 'image_request',
      content: prompt!,
    };

    const context = this.useContentContext() ? this.contentStateService.getFullContentText() : '';
    const externalContexts = this.attachedExternalContexts();
    const attachedContentIds = externalContexts.map((c) => c.id);

    this.conversationService
      .addMessageAndGenerateResponse(this.contentID(), userMessage, context, [], attachedContentIds, {
        allowAiSearch: this.allowAiSearch(),
        aiSkillztankBoundary: this.aiSkillztankBoundary(),
        searchAllContent: this.searchAllContent(),
        courseId: this.contentStateService.courseId,
        aspectRatio: aspectRatio!,
        model: model!,
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.scrollToBottom();
        })
      )
      .subscribe({
        next: () => {},
        error: (err) => {
          console.error('Image generation failed:', err);
          this.errorHandler.handleAiRestrictedError(err);
        },
      });

    this.imageForm.reset({ prompt: '', aspectRatio: '16:9', model: 'pro' });
  }

  cancelContent(): void {
    this.activeInputMode = 'text';
    this.contentForm.reset({
      subject: '',
      customization: '',
      model: 'fast',
      template: localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none',
    });
  }

  submitContent(): void {
    if (this.contentForm.invalid) {
      this.contentForm.markAllAsTouched();
      return;
    }

    const { subject, customization, model, template } = this.contentForm.getRawValue();
    this.activeInputMode = 'text';
    this.loading.set(true);

    if (template && template !== 'none') {
      localStorage.setItem('skz_last_selected_text_gen_template_id', template);
    }

    const userMessage: ConversationMessage = {
      role: 'user',
      type: 'content_request',
      content: subject!,
      context: JSON.stringify({ customization, model, template }),
    };

    const context = this.useContentContext() ? this.contentStateService.getFullContentText() : '';
    const externalContexts = this.attachedExternalContexts();
    const attachedContentIds = externalContexts.map((c) => c.id);

    this.conversationService
      .addMessageAndGenerateResponse(this.contentID(), userMessage, context, [], attachedContentIds, {
        allowAiSearch: this.allowAiSearch(),
        aiSkillztankBoundary: this.aiSkillztankBoundary(),
        searchAllContent: this.searchAllContent(),
        courseId: this.contentStateService.courseId,
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.scrollToBottom();
        })
      )
      .subscribe({
        next: () => {},
        error: (err) => {
          console.error('Content generation failed:', err);
          this.errorHandler.handleAiRestrictedError(err);
        },
      });

    this.contentForm.reset({
      subject: '',
      customization: '',
      model: 'fast',
      template: localStorage.getItem('skz_last_selected_text_gen_template_id') || 'none',
    });
  }

  cancelQuiz(): void {
    this.activeInputMode = 'text';
    this.quizForm.reset({ subject: '', count: 5, quizType: 'mixed', difficulty: 'medium', additionalContext: '' });
  }

  submitQuiz(): void {
    if (this.quizForm.invalid) {
      this.quizForm.markAllAsTouched();
      return;
    }

    const { subject, count, quizType, difficulty, additionalContext } = this.quizForm.getRawValue();
    this.activeInputMode = 'text';
    this.loading.set(true);

    const userMessage: ConversationMessage = {
      role: 'user',
      type: 'quiz_request',
      content: subject!,
      context: JSON.stringify({ count, quizType, difficulty, additionalContext }),
    };

    const context = this.useContentContext() ? this.contentStateService.getFullContentText() : '';
    const externalContexts = this.attachedExternalContexts();
    const attachedContentIds = externalContexts.map((c) => c.id);

    this.conversationService
      .addMessageAndGenerateResponse(this.contentID(), userMessage, context, [], attachedContentIds)
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.scrollToBottom();
        })
      )
      .subscribe({
        next: () => {},
        error: (err) => {
          console.error('Quiz generation failed:', err);
          this.errorHandler.handleAiRestrictedError(err);
        },
      });

    this.quizForm.reset({ subject: '', count: 5, quizType: 'mixed', difficulty: 'medium', additionalContext: '' });
  }

  insertMermaidChunk(mermaidContent: string, title: string): void {
    this.loading.set(true);
    const chunkParams = {
      courseID: this.contentStateService.courseId,
      contentID: this.contentID(),
      lecture: this.contentStateService.lecture,
      section: this.contentStateService.section,
      noteID: this.contentStateService.noteId,
      blogID: this.contentStateService.blogId,
      contentName: this.contentStateService.contentName,
      contentType: this.contentStateService.contentType,
      type: 'code',
      codeLanguage: 'mermaid',
      language: this.translate.currentLang,
      content: mermaidContent,
      title: title,
      tags: [],
      generateTranslation: false,
      generateVoice: false,
      generateEmbeddings: false,
    };

    const insertBelowID = this.paneData()?.chunkAboveId || undefined;

    this.chunkService
      .createChunk({
        chunks: [chunkParams],
        insertBelowID,
      })
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.toast.snackbar.open(this.translate.instant('Global.Saved'), 'Close', { duration: 2000 });
          this.closed.emit({ action: 'add-mermaid' });
        },
        error: (error) => {
          console.error('Failed to create mermaid chunk', error);
          this.errorHandler.handleAiRestrictedError(error);
          this.loading.set(false);
        },
      });
  }

  openMermaidPreview(message: any): void {
    const mermaidContent = message.content?.mermaid || '';
    if (!mermaidContent.trim()) return;

    const dialogId = message._id || Date.now().toString();
    const mermaidDialogRef = this.dialog.open(MermaidDialogComponent, {
      data: {
        content: mermaidContent,
        title: message.content?.title || 'Mermaid Diagram',
        id: dialogId,
      },
      panelClass: 'mermaid-dialog-panel',
      width: '95vw',
      height: '95vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
    });

    mermaidDialogRef.afterClosed().subscribe((insertRequested: boolean) => {
      if (insertRequested) {
        this.insertMermaidChunk(mermaidContent, message.content?.title || 'Mermaid Diagram');
      }
    });
  }

  insertMermaidFromMessage(message: any): void {
    const mermaidContent = message.content?.mermaid || '';
    if (!mermaidContent.trim()) return;
    this.insertMermaidChunk(mermaidContent, message.content?.title || 'Mermaid Diagram');
  }

  openCanvas(message: any): void {
    this.contentStateService.openCanvas({ ...message.content, messageId: message._id, contentId: this.contentID() });
  }

  getFileNameFromUrl(url: string): string {
    try {
      if (url.startsWith('gs://')) {
        const parts = url.split('/');
        const filename = parts[parts.length - 1];
        // remove timestamp if it exists (e.g., 1776174198964_filename.pdf -> filename.pdf)
        const match = filename.match(/^\d+_(.+)$/);
        return match ? match[1] : filename;
      }
      return url.split('/').pop() || 'File';
    } catch {
      return 'File';
    }
  }

  async downloadAttachment(url: string) {
    try {
      let downloadUrl = url;
      if (url.startsWith('gs://')) {
        const storageRef = ref(this.storage, url);
        downloadUrl = await getDownloadURL(storageRef);
      }
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Failed to download attachment', err);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
    }
  }

  getDownloadURL(filename: string): Promise<string> {
    const storageRef = ref(this.storage, filename);
    return getDownloadURL(storageRef);
  }

  openAspectRatioMenu(trigger: MatButton): void {
    const data: SelectListData = {
      title: 'FileUploader.Resolution',
      items: this.aspectRatios,
      selectedValue: this.selectedAspectRatio,
    };

    const { componentInstance } = this.overlay.open(trigger._elementRef, SelectListComponent, {
      hasBackdrop: true,
      data,
    });

    componentInstance.optionSelected.pipe(take(1)).subscribe((value) => {
      this.selectedAspectRatio = value;
      this.overlay.close();
      this.cdr.markForCheck();
    });
  }

  openGuidanceMenu(trigger: MatButton): void {
    const data: SelectListData = {
      title: 'MySpace.AIGuidanceScale.Title',
      items: this.guidanceScaleOptions,
      selectedValue: this.selectedGuidanceScale,
    };

    const { componentInstance } = this.overlay.open(trigger._elementRef, SelectListComponent, {
      hasBackdrop: true,
      data,
    });

    componentInstance.optionSelected.pipe(take(1)).subscribe((value) => {
      this.selectedGuidanceScale = value;
      this.overlay.close();
      this.cdr.markForCheck();
    });
  }

  getSelectedLabel(options: { label: string; value: any }[], value: any): string {
    const selected = options.find((o) => o.value === value);
    return selected ? selected.label : '';
  }

  private scrollToBottom(): void {
    try {
      const container = this.scrollContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    } catch (err) {
      console.warn('Could not scroll to bottom:', err);
    }
  }

  // --- Pane Actions ---
  addTextToEditor(content: string): void {
    this.contentStateService.addTextToEditor(content);
    this.closed.emit(null);
  }

  handleInsert(data: any): void {
    if (typeof data === 'string') {
      this.addTextToEditor(data);
    } else {
      this.createQuizChunk(data);
    }
  }

  createChunk(content: string): void {
    this.contentStateService.createChunkWithText(content);
  }

  createQuizChunk(quizData: any): void {
    this.loading.set(true);
    const chunkParams = {
      courseID: this.contentStateService.courseId,
      contentID: this.contentID(),
      lecture: this.contentStateService.lecture,
      section: this.contentStateService.section,
      noteID: this.contentStateService.noteId,
      blogID: this.contentStateService.blogId,
      contentName: this.contentStateService.contentName,
      contentType: this.contentStateService.contentType,
      type: 'quiz',
      language: this.translate.currentLang,
      content: quizData,
      tags: [],
      generateTranslation: false,
      generateVoice: false,
      generateEmbeddings: false,
    };

    const insertBelowID = this.paneData()?.chunkAboveId || undefined;

    this.chunkService
      .createChunk({
        chunks: [chunkParams],
        insertBelowID,
      })
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.toast.snackbar.open(this.translate.instant('Global.QuizCreated'), 'Close', { duration: 2000 });
          this.closed.emit({ action: 'add-quiz' });
        },
        error: (error) => {
          console.error('Failed to create quiz chunk', error);
          this.errorHandler.handleAiRestrictedError(error);
          this.loading.set(false);
        },
      });
  }

  allFilesFullyUploaded(): boolean {
    return this.attachedFiles().every((file) => !file.uploading);
  }

  uploadImageAndCreateChunk(gcsPath: string, prompt: string): void {
    this.isUploading = true;
    const insertId = this.paneData()?.chunkAboveId || null;
    const fileName = prompt.replace(/\s+/g, '_').toLowerCase() + '.png';

    this.contentStateService.setUploadState({ status: 'creating', progress: 100, insertId, fileName });

    const data: CopyImageAndCreateChunkData = {
      sourceGcsPath: gcsPath,
      contentID: this.contentID(),
      insertBelowID: this.paneData()?.chunkAboveId || undefined,
      insertAsFirst: !this.paneData()?.chunkAboveId ? true : false,
      language: this.translate.currentLang,
    };

    this.chunkService.createChunkFromGCSCopy(data).subscribe({
      next: () => {
        this.isUploading = false;
        this.uploadSuccess = true;
        this.contentStateService.setUploadState(null);
        this.closed.emit({ action: 'add-image' });
      },
      error: (err) => {
        console.error('Copy failed', err);
        this.errorHandler.handle401Error(err);
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
        this.contentStateService.setUploadState(null);
        this.isUploading = false;
      },
    });
  }

  openClearConversationMenu(event: MouseEvent, trigger: MatIconButton): void {
    event.stopPropagation();
    this.confirmMenuService.confirm(trigger._elementRef, 'GenerateContent.ClearConversation', 'playlist_remove').subscribe(() => {
      this.clearConversation();
    });
  }

  private clearConversation(): void {
    this.conversationService.clearConversation(this.contentID()).subscribe({
      next: () => {},
      error: (err) => {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
      },
    });
  }
  removeExternalContext(id: string) {
    this.attachedExternalContexts.update((contexts) => contexts.filter((c) => c.id !== id));
  }

  private cdkOverlay = inject(Overlay);
  private searcherOverlayRef: OverlayRef | null = null;

  toggleContentSearcher() {
    if (this.searcherOverlayRef) {
      this.searcherOverlayRef.dispose();
      this.searcherOverlayRef = null;
      return;
    }

    const positionStrategy = this.cdkOverlay.position().global().centerHorizontally().centerVertically();

    this.searcherOverlayRef = this.cdkOverlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
    });

    const portal = new ComponentPortal(ContentSearcherComponent);
    const compRef = this.searcherOverlayRef.attach(portal);

    compRef.instance.itemSelected.subscribe((result: ContentSearchResult) => this.onContentSearcherSelect(result));
    compRef.instance.closed.subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });

    this.searcherOverlayRef.backdropClick().subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });
  }

  async onContentSearcherSelect(result: ContentSearchResult) {
    if (!result?.id) return;
    if (this.attachedExternalContexts().find((c) => c.id === result.id)) return;

    this.attachedExternalContexts.update((contexts) => [...contexts, result]);
  }
}
